import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  IconButton,
  Stack,
} from '@mui/material';
import {
  AssessmentOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  DeleteOutlined,
  VisibilityOutlined,
  AddOutlined,
  UploadFileOutlined,
  BatchPredictionOutlined,
  QueueOutlined,
  PlayArrowOutlined,
  RefreshOutlined,
  HelpOutlineOutlined,
  SettingsOutlined,
  LanguageOutlined,
  TableChartOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../store/hooks';
import { bulkAnalysisApi, BulkAnalysisQuery } from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const UploadZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(37, 99, 235, 0.02)',
  '&:hover': {
    backgroundColor: 'rgba(37, 99, 235, 0.05)',
    borderColor: theme.palette.primary.dark,
    transform: 'scale(1.02)',
  },
  '&.drag-over': {
    backgroundColor: 'rgba(37, 99, 235, 0.1)',
    borderColor: theme.palette.primary.dark,
    transform: 'scale(1.02)',
  },
}));

interface AnalysisQuery {
  id: string;
  type: 'url' | 'csv';
  content: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  productsFound?: number;
  analysisStarted?: string;
  analysisCompleted?: string;
  error?: string;
}

interface BulkAnalysisConfig {
  enableLLMAnalysis: boolean;
  enableImageAnalysis: boolean;
  enableTextAnalysis: boolean;
  confidenceThreshold: number;
  autoMatchProducts: boolean;
  priority: 'low' | 'medium' | 'high';
  batchSize: number;
}

const BulkAnalysisPage: React.FC = () => {
  const { error } = useAppSelector((state) => state.analyses);
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [urls, setUrls] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [analysisQueries, setAnalysisQueries] = useState<AnalysisQuery[]>([]);
  const [analysisConfig, setAnalysisConfig] = useState<BulkAnalysisConfig>({
    enableLLMAnalysis: true,
    enableImageAnalysis: true,
    enableTextAnalysis: true,
    confidenceThreshold: 0.7,
    autoMatchProducts: true,
    priority: 'medium',
    batchSize: 10,
  });
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  // Navigation hook
  const navigate = useNavigate();

  // Load existing queries on component mount
  useEffect(() => {
    const loadQueries = async () => {
      try {
        const queries = await bulkAnalysisApi.getQueries();
        
        const mappedQueries: AnalysisQuery[] = queries.map(q => ({
          id: q.id,
          type: q.type,
          content: q.content?.substring(0, 50) + (q.content && q.content.length > 50 ? '...' : '') || '',
          status: q.status.toLowerCase() as 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled',
          productsFound: q.total_products,
          analysisStarted: q.created_at,
          analysisCompleted: q.completed_at,
          error: q.error_message,
        }));
        
        setAnalysisQueries(mappedQueries);
      } catch (error) {
        console.error('Failed to load queries:', error);
      }
    };
    
    loadQueries();
  }, []);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle file upload
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else if (file) {
      alert('Please select a CSV file');
    }
    if (e.target) e.target.value = '';
  }, []);

  // Handle drag and drop for CSV
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else if (file) {
      alert('Please drop a CSV file');
    }
  }, []);

  // Remove CSV file
  const removeCsvFile = useCallback(() => {
    setCsvFile(null);
  }, []);

  // Submit analysis query
  const handleSubmitAnalysis = useCallback(async () => {
    if (activeTab === 0 && !urls.trim()) {
      alert('Please enter URLs to analyze');
      return;
    }
    if (activeTab === 1 && !csvFile) {
      alert('Please upload a CSV file');
      return;
    }

    setProcessing(true);
    setProgress(0);

    try {
      const bulkQuery: BulkAnalysisQuery = {
        query_type: activeTab === 0 ? 'url' : 'csv',
        query_content: activeTab === 0 ? urls : undefined,
        urls: activeTab === 0 ? urls.split(',').map(url => url.trim()).filter(url => url) : undefined,
        csv_file: activeTab === 1 ? csvFile || undefined : undefined,
        llm_provider: 'openai',
        max_images_per_product: 3,
        priority: analysisConfig.priority,
        enableLLMAnalysis: analysisConfig.enableLLMAnalysis,
        enableImageAnalysis: analysisConfig.enableImageAnalysis,
        enableTextAnalysis: analysisConfig.enableTextAnalysis,
        confidenceThreshold: analysisConfig.confidenceThreshold,
        autoMatchProducts: analysisConfig.autoMatchProducts,
        batchSize: analysisConfig.batchSize,
      };

      const result = await bulkAnalysisApi.submitQuery(bulkQuery);

      const newQuery: AnalysisQuery = {
        id: result.query_id,
        type: activeTab === 0 ? 'url' : 'csv',
        content: activeTab === 0 ? urls : csvFile?.name || '',
        status: 'processing',
        analysisStarted: new Date().toISOString(),
      };

      setAnalysisQueries(prev => [...prev, newQuery]);
      
      // Clear form
      if (activeTab === 0) {
        setUrls('');
      } else {
        setCsvFile(null);
      }

      // Start polling for updates
      pollQueryStatus(result.query_id);
      
    } catch (error) {
      console.error('Failed to submit analysis query:', error);
      alert('Failed to submit analysis query. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [activeTab, urls, csvFile, analysisConfig]);

  // Poll query status
  const pollQueryStatus = useCallback(async (queryId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await bulkAnalysisApi.getStatus(queryId);
        
        setAnalysisQueries(prev => prev.map(q => 
          q.id === queryId ? {
            ...q,
            status: status.status.toLowerCase() as 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled',
            productsFound: status.total_products,
            analysisCompleted: status.completed_at,
            error: status.error_message,
          } : q
        ));
        
        setProgress(status.progress_percentage);
        
        if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'CANCELLED') {
          clearInterval(interval);
          setProgress(0);
        }
      } catch (error) {
        console.error('Error polling status:', error);
        clearInterval(interval);
      }
    }, 3000);
  }, []);

  // View analysis results
  const handleViewResults = useCallback((queryId: string) => {
    // Navigate to results page using React Router
    navigate(`/bulk-analysis-results/${queryId}`);
  }, [navigate]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined />;
      case 'processing': return <CircularProgress size={16} />;
      case 'failed': return <ErrorOutlineOutlined />;
      default: return <QueueOutlined />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Bulk Analysis Query
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Submit bulk analysis queries by providing URLs or CSV data. The system will automatically match products and perform counterfeit detection.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BatchPredictionOutlined sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Submit Analysis Query
                </Typography>
              </Box>

              {/* Input Method Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab 
                    icon={<LanguageOutlined />} 
                    label="URL Scraping" 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<TableChartOutlined />} 
                    label="CSV Upload" 
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              {/* URL Input Tab */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Product URLs to Analyze
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter comma-separated URLs of products you want to analyze for counterfeit detection.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    placeholder="https://example.com/product1, https://example.com/product2, https://example.com/product3"
                    value={urls}
                    onChange={(e) => setUrls(e.target.value)}
                    helperText="Enter URLs separated by commas. The system will scrape product information and automatically match with reference products."
                  />
                  
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Supported: Amazon, eBay, Walmart, and other major e-commerce sites
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* CSV Upload Tab */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Upload CSV File
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload a CSV file containing product information for bulk analysis.
                  </Typography>
                  
                  {!csvFile ? (
                    <UploadZone
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadFileOutlined sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Drop CSV file here or click to upload
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        CSV should contain: product_name, brand, category, url (optional)
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Select CSV File
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                    </UploadZone>
                  ) : (
                    <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: 'rgba(37, 99, 235, 0.05)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TableChartOutlined sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {csvFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(csvFile.size / 1024).toFixed(2)} KB
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton onClick={removeCsvFile} color="error">
                          <DeleteOutlined />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Expected CSV Format:
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: '#f8fafc', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      product_name,brand,category,url<br/>
                      "iPhone 14 Pro","Apple","Electronics","https://example.com/iphone14"<br/>
                      "Nike Air Max","Nike","Footwear","https://example.com/nike-airmax"
                    </Paper>
                  </Box>
                </Box>
              )}

              {/* Submit Button */}
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowOutlined />}
                  onClick={handleSubmitAnalysis}
                  disabled={processing || (activeTab === 0 && !urls.trim()) || (activeTab === 1 && !csvFile)}
                >
                  {processing ? 'Processing...' : 'Submit Analysis Query'}
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<HelpOutlineOutlined />}
                >
                  View Sample Format
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Configuration Panel */}
        <Grid item xs={12} lg={4}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsOutlined sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Analysis Configuration
                </Typography>
              </Box>

              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={analysisConfig.autoMatchProducts}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        autoMatchProducts: e.target.checked
                      }))}
                    />
                  }
                  label="Auto-match with reference products"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={analysisConfig.enableLLMAnalysis}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        enableLLMAnalysis: e.target.checked
                      }))}
                    />
                  }
                  label="LLM Analysis"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={analysisConfig.enableImageAnalysis}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        enableImageAnalysis: e.target.checked
                      }))}
                    />
                  }
                  label="Image Analysis"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={analysisConfig.enableTextAnalysis}
                      onChange={(e) => setAnalysisConfig(prev => ({
                        ...prev,
                        enableTextAnalysis: e.target.checked
                      }))}
                    />
                  }
                  label="Text Analysis"
                />
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Confidence Threshold
                </Typography>
                <TextField
                  type="number"
                  value={analysisConfig.confidenceThreshold}
                  onChange={(e) => setAnalysisConfig(prev => ({
                    ...prev,
                    confidenceThreshold: parseFloat(e.target.value)
                  }))}
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  size="small"
                  fullWidth
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Priority Level
                </Typography>
                <TextField
                  select
                  value={analysisConfig.priority}
                  onChange={(e) => setAnalysisConfig(prev => ({
                    ...prev,
                    priority: e.target.value as 'low' | 'medium' | 'high'
                  }))}
                  size="small"
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </TextField>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      {processing && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Processing analysis query... {Math.round(progress)}%
          </Typography>
        </Box>
      )}

      {/* Analysis Queries History */}
      {analysisQueries.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentOutlined sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2">
                    Analysis Queries History
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshOutlined />}
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </Button>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Query Type</TableCell>
                      <TableCell>Content</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Products Found</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysisQueries.map((query) => (
                      <TableRow 
                        key={query.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewResults(query.id)}
                      >
                        <TableCell>
                          <Chip
                            icon={query.type === 'url' ? <LanguageOutlined /> : <TableChartOutlined />}
                            label={query.type === 'url' ? 'URL Scraping' : 'CSV Upload'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {query.content}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(query.status)}
                            label={query.status.toUpperCase()}
                            color={getStatusColor(query.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {query.productsFound ? (
                            <Typography variant="body2">
                              {query.productsFound} products
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {query.analysisStarted ? new Date(query.analysisStarted).toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewResults(query.id);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default BulkAnalysisPage; 